import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { EMAIL_COLUMNS, EmailItem, useContactsByEmail, type EmailRecord } from "./EmailList";
import { ContactFromEmail } from "./ContactFromEmail";
import { EmptyState } from "../misc/EmptyState";
import { MailCheck } from "lucide-react";
import { useSwipe } from "../misc/useSwipe";

type Filter = "offen" | "aktiv" | "alle" | "ignoriert";

const AssignActions = ({
  email,
  companies,
  onDone,
}: {
  email: EmailRecord;
  companies: { id: number; name: string }[];
  onDone: (removed: boolean) => void;
}) => {
  const notify = useNotify();
  const [companyId, setCompanyId] = useState<string>(email.company_id ? String(email.company_id) : "");
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  const update = async (values: Partial<EmailRecord>, message: string) => {
    const removed = values.review_status !== undefined || values.company_id !== undefined;
    setBusy(true);
    const { error } = await getSupabaseClient().from("emails").update(values).eq("id", email.id);
    setBusy(false);
    if (error) {
      notify(`Fehler: ${error.message}`, { type: "error" });
      return;
    }
    notify(message, { type: "success" });
    onDone(removed);
  };

  return (
    <div className="space-y-2">
    {creating && <ContactFromEmail email={email} onDone={() => { setCreating(false); onDone(true); }} />}
    <div className="flex flex-wrap items-center gap-2">
      {!email.contact_id && (
        <Button size="sm" variant={creating ? "secondary" : "default"} data-shortcut="n" onClick={() => setCreating(!creating)}>
          {creating ? "Formular schließen" : "Kontakt aus Mail anlegen"}
        </Button>
      )}
      <select
        value={companyId}
        onChange={(e) => setCompanyId(e.target.value)}
        className="h-9 rounded-md border border-input bg-background px-3 text-sm"
      >
        <option value="">Firma wählen …</option>
        {companies.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <Button
        size="sm"
        disabled={!companyId || busy}
        onClick={() =>
          update({ company_id: Number(companyId), review_status: "zugeordnet" }, "E-Mail der Firma zugeordnet")
        }
      >
        Firma zuordnen
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        data-shortcut="g"
        onClick={() => update({ review_status: "geprueft" }, "Als geprüft markiert")}
      >
        Geprüft
      </Button>
      <Button size="sm" variant="ghost" disabled={busy} data-shortcut="i" onClick={() => update({ review_status: "ignoriert" }, "E-Mail ausgeblendet")}>
        Ignorieren
      </Button>
    </div>
    </div>
  );
};

const Kbd = ({ children }: { children: React.ReactNode }) => (
  <kbd className="rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[11px] text-foreground">{children}</kbd>
);

export const EmailInbox = () => {
  const [filter, setFilter] = useState<Filter>("offen");
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();

  const { data: emails, isPending } = useQuery({
    queryKey: ["emails-inbox", filter],
    queryFn: async () => {
      let q = getSupabaseClient().from("emails").select(EMAIL_COLUMNS).order("sent_at", { ascending: false }).limit(500);
      if (filter === "offen") q = q.is("company_id", null).is("contact_id", null).eq("review_status", "neu");
      if (filter === "ignoriert") q = q.eq("review_status", "ignoriert");
      // Aktiv = schon geprüft/zugeordnet und nicht ignoriert (alles außer „Zu prüfen“ und „Ignoriert“)
      if (filter === "aktiv")
        q = q.neq("review_status", "ignoriert").or("company_id.not.is.null,contact_id.not.is.null,review_status.neq.neu");
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as EmailRecord[];
    },
  });

  const { data: companies } = useQuery({
    queryKey: ["companies-for-email-assign"],
    queryFn: async () => {
      const { data } = await getSupabaseClient().from("companies").select("id,name").order("name");
      return (data ?? []) as { id: number; name: string }[];
    },
  });

  const { data: contactsByEmail } = useContactsByEmail();

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return emails ?? [];
    return (emails ?? []).filter((e) =>
      [e.subject, e.from_address, e.from_name, ...(e.to_addresses ?? []), e.snippet]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term)),
    );
  }, [emails, search]);

  // Geöffnete Mail steuern: ← → blättern, nach Ignorieren/Zuordnen direkt die nächste öffnen
  const [openId, setOpenId] = useState<number | null>(null);
  const nextAfterRemoval = useRef<number | null>(null);

  const move = (step: number) => {
    if (!visible.length) return;
    const index = visible.findIndex((e) => e.id === openId);
    const target = index === -1 ? (step > 0 ? 0 : visible.length - 1) : Math.min(Math.max(index + step, 0), visible.length - 1);
    setOpenId(visible[target].id);
  };

  // Handy: in der geöffneten Mail nach links wischen = nächste Mail, nach rechts = vorige
  useSwipe({ enabled: openId != null, onLeft: () => move(1), onRight: () => move(-1) });

  useEffect(() => {
    if (openId == null) return;
    document.getElementById(`email-${openId}`)?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [openId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      // Kein Blättern/Ignorieren, solange ein Fenster (z. B. Hilfe) oder Menü offen ist
      if (document.querySelector('[role="dialog"][data-state="open"], [role="menu"], [role="listbox"]')) return;
      if (event.key === "ArrowRight" || event.key === "ArrowDown" || event.key.toLowerCase() === "j") {
        event.preventDefault();
        move(1);
      } else if (event.key === "ArrowLeft" || event.key === "ArrowUp" || event.key.toLowerCase() === "k") {
        event.preventDefault();
        move(-1);
      } else if (event.key === "Escape") {
        setOpenId(null);
      } else if (["i", "g", "n"].includes(event.key.toLowerCase()) && openId != null) {
        // Groß- und Kleinschreibung (auch mit Feststelltaste) funktionieren gleich
        event.preventDefault();
        document.querySelector<HTMLButtonElement>(`#email-${openId} [data-shortcut="${event.key.toLowerCase()}"]`)?.click();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Nach dem Neuladen die gemerkte nächste Mail öffnen (die bearbeitete ist aus „Zu prüfen“ verschwunden)
  useEffect(() => {
    if (nextAfterRemoval.current == null) return;
    const wanted = nextAfterRemoval.current;
    if (visible.some((e) => e.id === wanted)) {
      setOpenId(wanted);
      nextAfterRemoval.current = null;
      requestAnimationFrame(() => document.getElementById(`email-${wanted}`)?.scrollIntoView({ block: "start", behavior: "smooth" }));
    }
  }, [visible]);

  const handleDone = (email: EmailRecord, removed: boolean) => {
    if (removed) {
      const index = visible.findIndex((e) => e.id === email.id);
      const next = visible[index + 1] ?? visible[index - 1];
      nextAfterRemoval.current = next?.id ?? null;
      setOpenId(filter === "alle" ? email.id : next?.id ?? null);
    }
    refresh();
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["emails-inbox"] });
    queryClient.invalidateQueries({ queryKey: ["emails"] });
    queryClient.invalidateQueries({ queryKey: ["contacts-by-email"] });
  };

  return (
    <div className="mt-4 max-w-[1800px] mx-auto">
      <Card>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="font-display text-2xl font-semibold">E-Mails</h1>
              <p className="text-sm text-muted-foreground">
                Aus den Postfächern abgerufen. „Zu prüfen“ zeigt Mails, die noch keiner Firma und keinem Kontakt zugeordnet sind, „Aktiv“ die zugeordneten (ohne ignorierte). Grüner Punkt = Adresse ist schon als Kontakt angelegt.
              </p>
            </div>
            <div className="flex gap-1 rounded-full bg-muted p-1">
              {(["offen", "aktiv", "alle", "ignoriert"] as Filter[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`rounded-full px-3 py-1 text-sm cursor-pointer ${filter === f ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
                >
                  {f === "offen" ? "Zu prüfen" : f === "aktiv" ? "Aktiv" : f === "alle" ? "Alle" : "Ignoriert"}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Schnell abarbeiten:</span>
            <span>Mail anklicken oder <Kbd>→</Kbd> drücken</span>
            <span><Kbd>←</Kbd> <Kbd>→</Kbd> vorige / nächste Mail</span>
            <span><Kbd>I</Kbd> oder <Kbd>i</Kbd> ignorieren</span>
            <span><Kbd>G</Kbd> oder <Kbd>g</Kbd> geprüft</span>
            <span><Kbd>N</Kbd> Kontakt aus Mail anlegen</span>
            <span><Kbd>Esc</Kbd> zuklappen</span>
            <span>Nach Ignorieren, Geprüft oder Zuordnen öffnet sich automatisch die nächste Mail.</span>
          </div>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen nach Betreff, Absender, Empfänger …"
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          />
          {isPending ? (
            <p className="py-6 text-sm text-muted-foreground">Wird geladen …</p>
          ) : visible.length ? (
            <div>
              <p className="text-xs text-muted-foreground">{visible.length} E-Mails</p>
              {visible.map((email) => (
                <EmailItem
                  key={email.id}
                  email={email}
                  contactsByEmail={contactsByEmail}
                  open={openId === email.id}
                  onToggle={() => setOpenId(openId === email.id ? null : email.id)}
                  actions={<AssignActions email={email} companies={companies ?? []} onDone={(removed) => handleDone(email, removed)} />}
                />
              ))}
            </div>
          ) : (
            <EmptyState Icon={MailCheck} title={filter === "offen" ? "Alles geprüft" : "Keine E-Mails"} text={filter === "offen" ? "Alle Mails sind einer Firma oder einem Kontakt zugeordnet." : undefined} />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

EmailInbox.path = "/emails";
